import Heading from 'components/Luxkit/Typography/Heading'
import Group from 'components/utils/Group'
import React, { useCallback, useContext, useMemo } from 'react'
import TextButton from 'components/Luxkit/Button/TextButton'
import * as Analytics from 'analytics/analytics'
import ValuePropositionItems from 'home/components/ValuePropositionBanner/ValuePropositionItems'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import GeoContext from 'contexts/geoContext'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import getStaticPagesByType from 'lib/utils/getStaticPagesByType'

import LinePhoneIcon from 'components/Luxkit/Icons/line/LinePhoneIcon'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import LineTagAltIcon from 'components/Luxkit/Icons/line/LineTagAltIcon'
import ContactContext from 'contexts/contactContext'
import TextLink from 'components/Luxkit/TextLink'
import LineUsersMultiIcon from 'components/Luxkit/Icons/line/LineUsersMultiIcon'

function getContent(regionCode: string, supportContact: App.GeoContacts) {
  return [
    {
      key: 'exclusive',
      heading: 'Exclusive deals & best price guarantee',
      body: 'Handpicked offers at unbeatable prices.',
      icon: <LineTagAltIcon/>,
      show: true,
    },
    {
      key: 'customerService',
      heading: '24/7 travel consultants',
      body: <Group direction="vertical">Call us anytime, before or after you book. <TextLink href={`tel:${supportContact.local.number}`}>{supportContact.local.humanReadable}</TextLink></Group>,
      icon: <LinePhoneIcon />,
      show: !NO_24_7_SUPPORT_REGIONS.includes(regionCode),
    },
    {
      key: 'trust',
      heading: 'Most trusted',
      body: regionCode === 'SG' ? 'Trusted by 100,000+ Singaporeans.' : 'Trusted by 8 million members.',
      icon: <LineUsersMultiIcon/>,
      show: true,
    },
  ]
}

interface Props {
  loggedIn?: boolean
}
function FrontPageUSPComponent({ loggedIn = false } : Props) {
  const { currentRegionCode } = useContext(GeoContext)
  const supportContact = useContext(ContactContext)
  const uspItems = useMemo(() => getContent(currentRegionCode, supportContact).filter(value => value.show), [currentRegionCode, supportContact])

  const showModal = useContext(ModalContext)
  const isAboutUsPageAvailable = useMemo<boolean>(() => !!getStaticPagesByType(['aboutUs']).length, [])

  const onLearnMoreClick = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'learn_about_us',
      action: 'clicked',
      category: 'usp_about_us',
      type: 'interaction',
    })
  }, [])

  const onSignupClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  return <VerticalSpacer gap={32} >
    <Heading variant="heading2">
      Why book with Luxury Escapes?
    </Heading>
    <CSSBreakpoint max="mobile">
      <Group gap={8} direction="vertical">
        {uspItems.map(({ icon, body, key }) => (
          <BodyTextBlock
            key={key}
            variant="medium"
            colour="neutral-one"
            startIcon={<icon.type {...icon.props} size="S" />}
          >
            {body}
          </BodyTextBlock>
        ))}
      </Group>
    </CSSBreakpoint>
    <CSSBreakpoint min="tablet">
      <ValuePropositionItems values={uspItems} />
    </CSSBreakpoint>
    {!loggedIn && <Group
      gap={8}
      direction="vertical-reverse"
      tabletDirection="horizontal"
      tabletHorizontalAlign="end"
    >
      {isAboutUsPageAvailable &&
        <TextButton kind="tertiary" onClick={onLearnMoreClick} to="/about-us" target="_blank">
          Learn more about us
        </TextButton>
      }
      <TextButton kind="primary" onClick={onSignupClick}>
        Sign up and save
      </TextButton>
    </Group>}
  </VerticalSpacer>
}

export default React.memo(FrontPageUSPComponent)
